import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, HelpCircle, Youtube, Sparkles, Clock, Play, ChevronRight, Award, Users, GraduationCap } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/utils-vr";
import AdSlot from "@/components/vr/AdSlot";
import Seo, { orgLd, websiteLd, faqLd } from "@/components/vr/Seo";

const DEFAULT_HERO_IMAGE = "https://customer-assets-cm19k8pv.emergentagent.net/job_board-exam-hub-46/artifacts/v90w7xok_HERO%20IMAGE.webp";

const DEFAULT_HOME = {
  hero_hindi_heading_line1: "बिहार बोर्ड की तैयारी",
  hero_hindi_heading_line2: "अब आसान तरीके से",
  hero_description: "Class 9 से 12 तक Notes, MCQ, Important Questions, Online Tests और Board Exam Preparation — सब कुछ Hindi Medium में, बिल्कुल मुफ़्त।",
  hero_badge_text: "बिहार बोर्ड 2027 विशेष तैयारी",
  cta_notes_label: "Free Study Material", cta_notes_link: "/notes",
  cta_quiz_label: "Start Quiz", cta_quiz_link: "/mcq",
  cta_youtube_label: "YouTube Classes", cta_youtube_link: "",
  cta_course_label: "English Course", cta_course_link: "/english-course",
  bihar_2027_subheading: "बोर्ड 2027 विशेष", bihar_2027_heading: "Bihar Board 2027 Preparation",
  stats: [
    { label: "Notes", value: "500+", icon: "BookOpen", active: true, order: 1 },
    { label: "MCQ Qs", value: "1000+", icon: "HelpCircle", active: true, order: 2 },
    { label: "Students", value: "10k+", icon: "Users", active: true, order: 3 },
  ],
  section_visibility: {
    popular_classes: true, bihar_2027: true, popular_subjects: true,
    latest_notes: true, mcq_quizzes: true, youtube_classes: true,
    course_cta: true, latest_blog: true, faqs: true,
  },
};

export default function Home() {
  const [data, setData] = useState({ classes: [], subjects: [], notes: [], quizzes: [], tests: [], videos: [], blog: [], faqs: [] });
  const [settings, setSettings] = useState({});
  const [home, setHome] = useState(DEFAULT_HOME);
  useEffect(() => {
    Promise.all([
      api.get("/classes"), api.get("/subjects"), api.get("/notes?limit=6"),
      api.get("/quizzes"), api.get("/tests"), api.get("/videos?limit=6"),
      api.get("/blog"), api.get("/faqs"), api.get("/settings/public"),
      api.get("/homepage"),
    ]).then(([c, s, n, q, t, v, b, f, st, h]) => {
      setData({ classes: c.data, subjects: s.data, notes: n.data, quizzes: q.data, tests: t.data, videos: v.data, blog: b.data, faqs: f.data });
      setSettings(st.data || {});
      // Merge saved homepage over defaults so missing keys still render
      setHome({ ...DEFAULT_HOME, ...(h.data || {}), section_visibility: { ...DEFAULT_HOME.section_visibility, ...((h.data || {}).section_visibility || {}) } });
    }).catch(() => {});
  }, []);

  const vis = home.section_visibility;
  const youtubeHref = home.cta_youtube_link || settings.youtube_channel_url || "#";
  const activeStats = (home.stats || []).filter((s) => s.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));

  const seoTitle = settings.site_title
    ? `${settings.site_title} — Bihar Board Class 9-12 Notes, MCQ & YouTube Classes`
    : "VR Classes — Bihar Board Class 9-12 Notes, MCQ & YouTube Classes";
  const seoDesc = settings.tagline || home.hero_description;
  const ldItems = [orgLd(), websiteLd()];
  if (data.faqs && data.faqs.length) {
    ldItems.push(faqLd(data.faqs.map((f) => ({ q: f.question, a: f.answer }))));
  }

  return (
    <div>
      <Seo title={seoTitle} description={seoDesc} canonical="/" jsonLd={ldItems} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white border-b border-slate-200">
        <div className="vr-container py-10 sm:py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-[#DC2626] text-xs font-bold" data-testid="hero-badge">
              <Sparkles size={14} /> {home.hero_badge_text}
            </div>
            <h1 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
              <span className="font-hindi">{home.hero_hindi_heading_line1}</span><br />
              <span className="font-hindi text-[#1E3A8A]">{home.hero_hindi_heading_line2}</span>
            </h1>
            <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-xl font-hindi leading-relaxed">
              {home.hero_description}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={home.cta_notes_link || "/notes"} className="vr-btn-primary" data-testid="hero-cta-notes"><BookOpen size={18} /> {home.cta_notes_label}</Link>
              <Link to={home.cta_quiz_link || "/mcq-quiz"} className="vr-btn-secondary" data-testid="hero-cta-quiz"><HelpCircle size={18} /> {home.cta_quiz_label}</Link>
              <a href={youtubeHref} target="_blank" rel="noreferrer" className="vr-btn-red" data-testid="hero-cta-youtube"><Youtube size={18} /> {home.cta_youtube_label}</a>
              <Link to={home.cta_course_link || "/english-course"} className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white font-semibold shadow-md hover:shadow-lg" data-testid="hero-cta-course"><Sparkles size={18} /> {home.cta_course_label}{settings.course_price_inr ? ` ₹${settings.course_price_inr}` : ""}</Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {activeStats.slice(0, 3).map((s, i) => {
                const Ic = LucideIcons[s.icon] || BookOpen;
                return (
                  <div key={i} className="text-center vr-card p-3" data-testid={`hero-stat-${i}`}>
                    <div className="w-8 h-8 mx-auto rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center"><Ic size={18} /></div>
                    <div className="mt-1 font-display font-extrabold text-lg text-[#0F172A]">{s.value}</div>
                    <div className="text-[11px] text-slate-500 font-medium">{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <img loading="lazy" src={resolveMediaUrl(home.hero_image) || DEFAULT_HERO_IMAGE} alt="VR Classes — Free Materials: Notes, MCQ Test, PYQs and Support for Bihar Board students" className="rounded-2xl shadow-xl aspect-[16/9] object-cover w-full" data-testid="hero-image" />
            <div className="absolute -bottom-4 -left-4 vr-card p-3 flex items-center gap-3 max-w-xs">
              <div className="w-10 h-10 rounded-lg bg-red-100 text-[#DC2626] flex items-center justify-center"><Award size={20} /></div>
              <div>
                <div className="text-sm font-bold text-[#0F172A]">Bihar Board 2027</div>
                <div className="text-xs text-slate-500">पूर्ण सिलेबस, मॉडल पेपर</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="vr-container"><AdSlot id="home-top" /></div>

      {/* POPULAR CLASSES */}
      {vis.popular_classes && <Section title="Popular Classes" hindi="कक्षा चुनें" link="/classes">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {data.classes.map((c) => (
            <Link key={c.id} to={`/classes/${c.slug}`} className="vr-card p-4 sm:p-5 hover:border-[#1E3A8A] group" data-testid={`class-card-${c.slug}`}>
              <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-black">{c.name.replace("Class ", "")}</div>
              <div className="mt-3 font-display font-bold text-lg text-[#0F172A]">{c.name}</div>
              <div className="text-sm text-slate-500 font-hindi">{c.hindi_name}</div>
              <div className="mt-3 flex items-center text-[#1E3A8A] font-semibold text-sm gap-1 group-hover:gap-2 transition-all">देखें <ChevronRight size={14} /></div>
            </Link>
          ))}
        </div>
      </Section>}

      {/* BIHAR BOARD 2027 */}
      {vis.bihar_2027 && <Section title={home.bihar_2027_heading} hindi={home.bihar_2027_subheading} link="/classes/class-10">
        <div className="grid md:grid-cols-3 gap-4">
          {[{ t: "Class 10 Matric", d: "पूर्ण सिलेबस के अनुसार निःशुल्क नोट्स, MCQ और मॉडल पेपर।", to: "/classes/class-10", c: "blue" },
            { t: "Class 12 Inter", d: "PCM/PCB/Commerce/Arts सभी स्ट्रीम की तैयारी।", to: "/classes/class-12", c: "red" },
            { t: "Mock Tests", d: "Chapter-wise और Full-syllabus Mock Tests टाइमर के साथ।", to: "/online-tests", c: "slate" }].map((s, i) => (
            <Link key={i} to={s.to} className="vr-card p-6 border-l-4" style={{ borderLeftColor: s.c === "red" ? "#DC2626" : s.c === "blue" ? "#1E3A8A" : "#64748B" }} data-testid={`bihar-2027-${i}`}>
              <div className="font-display font-bold text-lg text-[#0F172A]">{s.t}</div>
              <div className="mt-1 text-sm text-slate-600 font-hindi">{s.d}</div>
              <div className="mt-3 text-[#1E3A8A] font-semibold text-sm inline-flex items-center gap-1">शुरू करें <ChevronRight size={14} /></div>
            </Link>
          ))}
        </div>
      </Section>}

      {/* SUBJECTS */}
      {vis.popular_subjects && <Section title="Popular Subjects" hindi="प्रमुख विषय" link="/subjects">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {data.subjects.slice(0, 8).map((s) => (
            <Link key={s.id} to={`/subjects/${s.slug}`} className="vr-card p-4 hover:border-[#1E3A8A]" data-testid={`subject-card-${s.slug}`}>
              <div className="font-display font-bold text-[#0F172A]">{s.name}</div>
              <div className="text-xs text-slate-500 font-hindi">{s.hindi_name}</div>
            </Link>
          ))}
        </div>
      </Section>}

      {/* LATEST NOTES */}
      {vis.latest_notes && <Section title="Latest Notes" hindi="नए नोट्स" link="/notes">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.notes.map((n) => (
            <Link key={n.id} to={`/notes/${n.slug}`} className="vr-card p-5" data-testid={`note-card-${n.slug}`}>
              <span className="vr-chip">{n.class_slug?.replace("class-", "Class ")}</span>
              <div className="mt-2 font-display font-bold text-[#0F172A]">{n.title}</div>
              <div className="mt-1 text-sm text-slate-600 font-hindi line-clamp-2">{n.intro}</div>
              <div className="mt-3 text-[#1E3A8A] text-sm font-semibold inline-flex items-center gap-1">पढ़ें <ChevronRight size={14} /></div>
            </Link>
          ))}
        </div>
      </Section>}

      <div className="vr-container"><AdSlot id="home-mid" height="h-28" /></div>

      {/* QUIZZES */}
      {vis.mcq_quizzes && <Section title="MCQ Quizzes" hindi="MCQ क्विज़" link="/mcq-quiz">
        <div className="grid md:grid-cols-3 gap-4">
          {data.quizzes.slice(0, 6).map((q) => (
            <Link key={q.id} to={`/quiz/${q.slug}`} className="vr-card p-5 hover:border-[#DC2626]" data-testid={`quiz-card-${q.slug}`}>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Clock size={12} /> {q.duration_minutes} मिनट
              </div>
              <div className="mt-2 font-display font-bold text-[#0F172A]">{q.title}</div>
              <div className="mt-1 text-sm text-slate-600 font-hindi line-clamp-2">{q.description}</div>
              <div className="mt-3 inline-flex items-center gap-1 bg-[#DC2626] text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Play size={12} /> Start Quiz</div>
            </Link>
          ))}
        </div>
      </Section>}

      {/* YOUTUBE */}
      {vis.youtube_classes && <Section title="Latest YouTube Classes" hindi="YouTube क्लासेज़" link="/youtube-classes">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.videos.slice(0, 6).map((v) => (
            <a key={v.id} href={`https://youtu.be/${v.youtube_id}`} target="_blank" rel="noreferrer" className="vr-card overflow-hidden group" data-testid={`video-card-${v.id}`}>
              <div className="relative aspect-video bg-slate-100">
                <img loading="lazy" src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition">
                  <div className="w-12 h-12 rounded-full bg-[#DC2626] flex items-center justify-center"><Play className="text-white" size={22} /></div>
                </div>
              </div>
              <div className="p-4">
                <div className="font-semibold text-sm text-[#0F172A] line-clamp-2">{v.title}</div>
                <div className="text-xs text-slate-500 mt-1">{v.class_slug?.replace("class-", "Class ")} · {v.subject_slug}</div>
              </div>
            </a>
          ))}
        </div>
      </Section>}

      {/* COURSE CTA */}
      {vis.course_cta && <section className="my-16 vr-container">
        <div className="rounded-2xl bg-gradient-to-br from-[#1E3A8A] via-[#1e3272] to-[#0F172A] text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1 rounded-full text-xs font-bold"><Sparkles size={14} /> Special Course</div>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl">Bihar Board English Course 2027</h2>
            <p className="mt-3 text-blue-100 font-hindi text-lg">Grammar, Answer Writing, Important Questions, Easy Tricks एवं Board Strategy — मात्र ₹{settings.course_price_inr || 199} में।</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/english-course" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] font-semibold" data-testid="home-course-cta"><GraduationCap size={18} /> BUY COURSE NOW — ₹{settings.course_price_inr || 199}</Link>
              <Link to="/english-course" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white/10 border border-white/30 font-semibold hover:bg-white/20">More Details</Link>
            </div>
          </div>
        </div>
      </section>}

      {/* BLOG */}
      {vis.latest_blog && <Section title="Latest Blog Posts" hindi="ब्लॉग" link="/blog">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.blog.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="vr-card overflow-hidden" data-testid={`blog-card-${p.slug}`}>
              {p.featured_image && <img loading="lazy" src={resolveMediaUrl(p.featured_image)} alt={p.title} className="w-full aspect-video object-cover" />}
              <div className="p-4">
                <span className="vr-chip">{p.category}</span>
                <div className="mt-2 font-display font-bold text-[#0F172A]">{p.title}</div>
                <div className="mt-1 text-sm text-slate-600 line-clamp-2">{p.excerpt}</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>}

      {/* FAQs */}
      {vis.faqs && <section className="vr-container my-16" data-testid="home-faqs">
        <div className="mb-6">
          <div className="text-xs font-bold text-[#DC2626] uppercase tracking-widest">FAQ</div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F172A]">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {data.faqs.map((f, i) => (
            <details key={f.id} className="vr-card p-4 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-semibold text-[#0F172A] flex items-center justify-between">
                <span className="font-hindi">{f.question}</span>
                <ChevronRight size={16} className="group-open:rotate-90 transition" />
              </summary>
              <p className="mt-2 text-sm text-slate-600 font-hindi leading-relaxed">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>}
    </div>
  );
}

function Section({ title, hindi, link, children }) {
  return (
    <section className="vr-container my-12">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-xs font-bold text-[#DC2626] uppercase tracking-widest">{hindi}</div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F172A]">{title}</h2>
        </div>
        {link && <Link to={link} className="text-sm font-semibold text-[#1E3A8A] hover:underline hidden sm:inline-flex items-center gap-1">View All <ChevronRight size={14} /></Link>}
      </div>
      {children}
    </section>
  );
}
